import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PersonIcon from "@mui/icons-material/Person";
import { Box } from "@mui/material";
import capitalize from "lodash/capitalize";
import { useState } from "react";
import Flex from "../../../../../../components/Flex";
import OutlineCard from "../../../../../../components/OutlineCard";
import ProfileViewer from "../../../../../../components/ProfileViewer";
import Text from "../../../../../../components/Text";
import { formatDate } from "../../../../../../utils/date-time-utility";
import {
  getAddressFormat,
  // getTrimmedUuid,
  replaceUnderScoreAddSpace,
} from "../../../../../../utils/utils";

export interface RenderingProviderInfo {
  name?: string;
  providerId?: string;
  npi?: string;
  licenseNumber?: string;
  taxonomyNumber?: string;
}

interface PatientDetailsSideSectionForClaimProps {
  insuranceInfo?: any;
  insuranceType?: string;
  patientsData?: any;
  hideInsuranceInfo?: boolean;
  renderingProviderInfo?: RenderingProviderInfo;
  // The invoice and claim screens now render the same reduced field set (no
  // Provider ID, Taxonomy Number in place of License TX), so the flag that used
  // to switch between them has no remaining caller.
  // isInvoice?: boolean;
}

const PatientDetailsSideSectionForClaim = ({
  insuranceInfo,
  insuranceType,
  patientsData,
  hideInsuranceInfo = false,
  renderingProviderInfo,
}: PatientDetailsSideSectionForClaimProps) => {
  const initialExpandState = [true, true, !hideInsuranceInfo, false];
  const [isExpanded, setExpanded] = useState<boolean[]>(initialExpandState);

  const handleCollapse = (idx: number) => {
    const newArray = [...isExpanded];
    newArray[idx] = !newArray[idx];
    setExpanded(newArray);
  };

  return (
    <Flex flexDirection={"column"} gap={2}>
      <Box marginTop={"auto"}>
        {/* Patient Details Card */}
        <OutlineCard sx={{ mb: 1 }}>
          <Flex
            justifyContent={"space-between"}
            alignItems={"center"}
            bgcolor={"primary.light"}
            p={0.5}
            sx={{ cursor: "pointer" }}
            onClick={() => handleCollapse(1)}
          >
            <Flex alignItems={"center"} gap={1}>
              <PersonIcon color="primary" fontSize="small" />
              <Text variant="h6" fontWeight={"medium"}>
                Patient Details
              </Text>
            </Flex>
            <ExpandMoreIcon
              sx={{
                cursor: "pointer",
                transform: isExpanded[1] ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            />
          </Flex>
          {isExpanded[1] && (
            <Box p={2}>
              <ProfileViewer
                gap={"8px"}
                withoutColn
                firstColW={5.8}
                secondColW={6}
                data={{
                  "Patient ID": `${patientsData?.ecarehealthPatientId ?? ""}`,
                  Name: `${patientsData?.firstName ?? ""} ${
                    patientsData?.lastName ?? ""
                  }`,
                  Gender: `${
                    patientsData?.gender
                      ? replaceUnderScoreAddSpace(
                          patientsData.gender.toLowerCase()
                        )
                      : ""
                  }`,
                  DOB: `${
                    patientsData?.birthDate
                      ? formatDate(patientsData.birthDate)
                      : ""
                  }`,
                  "Contact Number": `${patientsData?.mobileNumber ?? ""}`,
                  Address: `${getAddressFormat(patientsData?.address) ?? ""}`,
                }}
              />
            </Box>
          )}
        </OutlineCard>

        {/* Insurance Information Card */}
        {!hideInsuranceInfo && (
          <OutlineCard sx={{ mb: 1 }}>
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              bgcolor={"primary.light"}
              p={0.5}
              sx={{ cursor: "pointer" }}
              onClick={() => handleCollapse(2)}
            >
              <Flex alignItems={"center"} gap={1}>
                <HealthAndSafetyIcon color="primary" fontSize="small" />
                <Text variant="h6" fontWeight={"medium"}>
                  Insurance Information
                </Text>
              </Flex>
              <ExpandMoreIcon
                sx={{
                  cursor: "pointer",
                  transform: isExpanded[2] ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              />
            </Flex>
            {isExpanded[2] && (
              <Box p={2}>
                <ProfileViewer
                  gap={"8px"}
                  withoutColn
                  firstColW={5.8}
                  secondColW={6}
                  data={{
                    "Insurance Type": `${capitalize(insuranceType) || "-"}`,
                    "Payer Name": `${
                      insuranceInfo?.insurancePayer?.payerName || ""
                    }`,
                    "Plan Name": `${insuranceInfo?.planName || ""}`,
                    "Insurance Id Number": `${
                      insuranceInfo?.insuranceId || ""
                    }`,
                    "Group ID": `${insuranceInfo?.groupId || ""}`,
                    "Effective End Date": `${
                      insuranceInfo?.effectiveEndDate
                        ? formatDate(insuranceInfo?.effectiveEndDate)
                        : ""
                    }`,
                    Eligibility: `${
                      patientsData?.eligibilityVerified ? "Verified" : ""
                    }`,
                  }}
                />
              </Box>
            )}
          </OutlineCard>
        )}

        {/* Provider Details Card — Rendering Provider for invoices, Primary Provider for claims */}
        <OutlineCard>
          <Flex
            justifyContent={"space-between"}
            alignItems={"center"}
            bgcolor={"primary.light"}
            p={0.5}
            sx={{ cursor: "pointer" }}
            onClick={() => handleCollapse(3)}
          >
            <Flex alignItems={"center"} gap={1}>
              <LocalHospitalIcon color="primary" fontSize="small" />
              <Text variant="h6" fontWeight={"medium"}>
                Rendering Provider Details
              </Text>
            </Flex>
            <ExpandMoreIcon
              sx={{
                cursor: "pointer",
                transform: isExpanded[3] ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            />
          </Flex>
          {isExpanded[3] && (
            <Box p={2}>
              <ProfileViewer
                gap={"8px"}
                withoutColn
                firstColW={5.8}
                secondColW={6}
                data={{
                  "Provider Name": renderingProviderInfo?.name ?? "-",
                  NPI: renderingProviderInfo?.npi ?? "-",
                  // eCarehealth Production - 10th July Tracker: Provider ID is
                  // dropped and License TX is replaced by the Taxonomy Number,
                  // on the claim screen as well as the invoice ones.
                  // "Provider ID": renderingProviderInfo?.providerId
                  //   ? (getTrimmedUuid(renderingProviderInfo.providerId) ?? "-")
                  //   : "-",
                  // "License TX": renderingProviderInfo?.licenseNumber ?? "-",
                  "Taxonomy Number":
                    renderingProviderInfo?.taxonomyNumber ?? "-",
                }}
              />
            </Box>
          )}
        </OutlineCard>
      </Box>
    </Flex>
  );
};

export default PatientDetailsSideSectionForClaim;
